import {
  Alert,
  Box,
  Divider,
  TextField,
  Typography,
  Grid2 as Grid,
} from "@mui/material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FC } from "react";
import { ACTIONS, STEPS } from "../config";
import { StepLayoutContainer } from "components/Layout/StepLayoutContainer";
import { ErrorType, RequestFeedbackData } from "../types";
import {
  getMaxObservationDate,
  getMinObservationDate,
} from "../observationDate";

type RequestPatientInfoStepProps = {
  onUpdate: (data: any) => void;
  data: RequestFeedbackData;
  error: ErrorType;
  handleStep: (step: string, action?: string) => void;
  isLearnerFlow?: boolean;
};

export const RequestPatientInfoStep: FC<RequestPatientInfoStepProps> = ({
  onUpdate,
  data,
  error,
  handleStep,
  isLearnerFlow = false,
}) => {
  return (
    <StepLayoutContainer
      title="Please Identify the Patient"
      onClickNext={() => handleStep(STEPS.REQUEST1)}
      onClickBack={() => handleStep(STEPS.REQUEST2, "back")}
    >
      <Box mt={2}>
        <Typography variant="body1">Identify the patient</Typography>
      </Box>
      <Box>
        <Grid container mt={2} spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Pet Name"
              name="name"
              value={data.name}
              onChange={(e) =>
                onUpdate({
                  type: ACTIONS.SET_NAME,
                  payload: e.target.value,
                })
              }
              margin="dense"
              fullWidth
              type="text"
              size="small"
              error={!!error.name}
              helperText={error.name}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Additional information"
              name="mrn"
              value={data.mrn}
              onChange={(e) =>
                onUpdate({ type: ACTIONS.SET_MRN, payload: e.target.value })
              }
              margin="dense"
              fullWidth
              type="text"
              size="small"
              multiline
              minRows={3}
              error={!!error.mrn}
              helperText={error.mrn}
            />
          </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Box>
          <Typography>
            Confirm when you worked with this patient by selecting the date and
            time below.
          </Typography>
        </Box>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {isLearnerFlow && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              To preserve accuracy and reduce recall bias, observation dates
              older than 6 days cannot be submitted.
            </Alert>
          )}
          <Grid container mt={2} spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker
                label="Observation Date*"
                value={data.caseDate}
                minDate={isLearnerFlow ? getMinObservationDate() : undefined}
                maxDate={isLearnerFlow ? getMaxObservationDate() : undefined}
                onChange={(newValue) =>
                  onUpdate({
                    type: ACTIONS.SET_CASE_DATE,
                    payload: newValue,
                  })
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    error: !!error.caseDate,
                    helperText: error.caseDate,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TimePicker
                label="Case Time"
                value={data.caseTime}
                onChange={(newValue) =>
                  onUpdate({
                    type: ACTIONS.SET_CASE_TIME,
                    payload: newValue,
                  })
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    error: !!error.caseTime,
                    helperText: error.caseTime,
                  },
                }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>
    </StepLayoutContainer>
  );
};

